import { useNavigate } from 'react-router-dom'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ComponentType,
  FC,
  useRef,
} from 'react'
import {
  getTargetUrl,
  Permission,
  Role,
  setTargetUrl,
} from './CompositeAuthProvider'
import { isValidToken, parseJwt } from '../utils/AuthUtil'
import { isDev } from '../utils/EnvUtil'
import Spinner from 'views/Spinner'
import { useApi } from './ApiProvider'
import { useArchitectureApi } from './ArchitectureApiProvider'
import { useQuizApi } from './QuizApiProvider'

export interface User {
  id: string
  name?: string
  firstName?: string
  lastName?: string
  username?: string // for backward compatibility
  email?: string
  roles?: string[]
  permissions?: string[]
}

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  error?: Error | undefined
}

interface UnswAuthContextProps {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  error?: Error | undefined
  initializeAuth: () => Promise<void>
  loginWithRedirect: (options?: { mode: string }) => Promise<void>
  logout: (logoutParams?: { returnTo?: string }) => void
  getAccessTokenSilently: () => Promise<string>
  getRole: () => Role
  withAuthenticationRequired: <P extends object>(
    Component: ComponentType<P>,
    options?: {
      onRedirecting?: () => React.ReactNode
      returnTo?: string | (() => string)
    },
  ) => FC<P>
  hasPermission: (permission: Permission) => boolean
}

export function generateRandomString(length: number = 32): string {
  const charset =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  const cryptoObj = window.crypto || (window as any).msCrypto
  const randomValues = new Uint32Array(length)
  cryptoObj.getRandomValues(randomValues)
  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length]
  }
  return result
}

export function generateState(): string {
  return generateRandomString(48)
}

const UnswAuthContext = createContext<UnswAuthContextProps | undefined>(
  undefined,
)
export const UNSW_AUTH_STORAGE_KEY = '@@unsw-auth@@'

const waitForAccessTokenInIframe = (): Promise<string | null> => {
  console.log(
    'Running inside an iframe, waiting for access token via postMessage...',
  )
  return new Promise<string | null>((resolve) => {
    const messageHandler = (event: MessageEvent) => {
      if (isDev) {
        console.log('Received message in iframe:', event.data)
      }
      if (event.data && event.data.type === 'UNSW_ACCESS_TOKEN') {
        window.removeEventListener('message', messageHandler)
        resolve(event.data.token || null)
      }
    }
    window.addEventListener('message', messageHandler)

    // Timeout in case no message is received
    setTimeout(() => {
      console.warn(
        'No access token received via postMessage within timeout period.',
      )
      window.removeEventListener('message', messageHandler)
      resolve(null)
    }, 15 * 1000) // Adjust timeout as needed
  })
}

const handleIframeAuth = async (
  setData: (token: string | null) => void,
): Promise<boolean> => {
  // Notify parent window that iframe is ready to receive token
  if (window.parent !== window) {
    window.parent.postMessage({ type: 'UNSW_IFRAME_READY' }, '*')
  }

  const token = await waitForAccessTokenInIframe()
  if (token && isValidToken(token)) {
    console.log('Access token received via postMessage')
    setData(token)
    return true
  } else {
    console.warn('Failed to receive valid access token via postMessage.')
    return false
  }
}

export const UnswAuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate()
  const accessTokenRef = useRef<string | null>(null)
  const { setToken } = useApi()
  const { setToken: setTokenForArchitectureApi } = useArchitectureApi()
  const { setToken: setTokenForQuizApi } = useQuizApi()

  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: undefined,
  })

  const hasPermission = (permission: Permission): boolean => {
    return (
      !!authState.user?.permissions &&
      authState.user.permissions.includes(permission)
    )
  }

  const getRole = (): Role => {
    if (authState.user) {
      const roles = authState.user.roles || []
      if (roles.includes(Role.SuperAdmin)) {
        return Role.SuperAdmin
      } else if (roles.includes(Role.Admin)) {
        return Role.Admin
      } else if (roles.includes(Role.Author)) {
        return Role.Author
      } else if (roles.includes(Role.Designer)) {
        return Role.Designer
      } else if (roles.includes(Role.Contractor)) {
        return Role.Contractor
      }
    }
    return Role.None
  }

  const setAccessToken = useCallback(
    (token: string | null): void => {
      if (token && isValidToken(token)) {
        accessTokenRef.current = token
        const payload = parseJwt(token)
        const currentUser = extractUserInfo(payload)
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: currentUser,
          error: undefined,
        })

        // Set the access token in the API clients
        setToken(token)
        setTokenForArchitectureApi(token)
        setTokenForQuizApi(token)
      } else {
        accessTokenRef.current = null
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: undefined,
        })
        // Clear the access token in the API clients
        setToken(null)
        setTokenForArchitectureApi(null)
        setTokenForQuizApi(null)
        console.warn('Invalid access token, clearing authentication state.')
      }
    },
    [setToken, setTokenForArchitectureApi, setTokenForQuizApi],
  )

  const parseLocalStorage = useCallback((): {
    access_token: string
    refresh_token: string
  } | null => {
    const storedData = localStorage.getItem(UNSW_AUTH_STORAGE_KEY)
    console.log('Parsing local storage for UNSW Auth data...')
    // console.log('Stored data:', storedData)
    if (storedData) {
      try {
        const data = JSON.parse(storedData)
        return {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        }
      } catch (error) {
        console.error('Error parsing local storage data:', error)
      }
    }
    return null
  }, [])

  const restoreAccessTokenFromStorage = useCallback((): string | null => {
    const data = parseLocalStorage()
    console.log('Restoring access token from local storage:', data)
    if (data) {
      console.log('Restoring access token from local storage...')
      const token = data.access_token
      if (token && isValidToken(token)) {
        setAccessToken(token)
        return token
      }
    }
    return null
  }, [parseLocalStorage, setAccessToken])

  const extractUserInfo = (payload: any): User | null => {
    if (!payload) return null
    console.log('payload:', payload)

    const email = Array.isArray(payload.emails)
      ? payload.emails[0]
      : payload.email
    // combine role and roles
    let roles = Array.isArray(payload.roles) ? payload.roles : []
    if (payload.role) {
      if (typeof payload.role === 'string') {
        roles.push(payload.role)
      }
    }

    // roles may be in payload['https://insights.dev.unsw.edu.au/roles]
    // Try to extract roles from any claim ending with '/roles'
    if (!roles || roles.length === 0) {
      const roleClaimKey = Object.keys(payload).find((key) =>
        key.endsWith('/roles'),
      )
      let extractedRoles = roleClaimKey ? payload[roleClaimKey] : []
      if (typeof extractedRoles === 'string') {
        extractedRoles = [extractedRoles]
      }
      if (Array.isArray(extractedRoles)) {
        roles = extractedRoles
          .map((role) => role.trim())
          .filter((role) => role.length > 0)
      }
    }

    // deduplicate roles
    const uniqueRoles = Array.from(new Set(roles))
    if (uniqueRoles.length > 0) {
      roles = uniqueRoles
    }

    // username is first_name and last_name
    const username =
      (payload.first_name || '') + ' ' + (payload.last_name || '').trim()
    return {
      id: payload.user_id,
      name: payload.name,
      firstName: payload.first_name,
      lastName: payload.last_name,
      username: username || undefined,
      email: email,
      roles: roles,
      permissions: payload.permissions || [],
    }
  }

  const getRefreshToken = useCallback((): string | null => {
    const data = parseLocalStorage()
    if (data) {
      const refreshToken = data.refresh_token
      if (refreshToken) {
        return refreshToken
      }
    }
    return null
  }, [parseLocalStorage])

  const refreshAccessToken = useCallback(
    async (refreshToken: string): Promise<string> => {
      console.log('Refreshing access token...')
      const api = `${process.env.REACT_APP_UNSW_AUTH_API_URL}/refresh-token`
      const response = await fetch(api, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: refreshToken,
          type: 'aad', // or 'b2c'
        }),
      })
      if (!response.ok) {
        console.error('Error refreshing access token:', response.statusText)
        throw new Error('Failed to refresh access token')
      }
      const data = await response.json()
      const token = data.access_token
      if (token && isValidToken(token)) {
        accessTokenRef.current = token
        const payload = parseJwt(token)
        setAuthState((prev) => ({
          ...prev,
          user: extractUserInfo(payload),
        }))
        localStorage.setItem(UNSW_AUTH_STORAGE_KEY, JSON.stringify(data))
        return token
      } else {
        console.error('Invalid access token received during refresh')
        throw new Error('Invalid access token received during refresh')
      }
    },
    [],
  )

  const getAccessToken = useCallback(
    async (
      code: string,
      codeVerifier: string,
      type: string,
    ): Promise<string> => {
      try {
        console.log('Fetching access token...')
        const api = `${process.env.REACT_APP_UNSW_AUTH_API_URL}/token`
        const response = await fetch(api, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            codeVerifier,
            type,
            redirectUri: `${process.env.REACT_APP_UNSW_AUTH_URL}/auth/callback`,
          }),
        })

        if (!response.ok) {
          throw new Error(
            `Failed to fetch access token: ${response.statusText}`,
          )
        }
        // console.log(response)
        console.log('Access token gotten successfully')
        const data = await response.json()
        localStorage.setItem(UNSW_AUTH_STORAGE_KEY, JSON.stringify(data))
        const token = data.access_token
        return token
      } catch (error) {
        console.error('Error fetching access token:', error)
        throw new Error('Unable to fetch access token')
      }
    },
    [],
  )

  const getAccessTokenForAuth0 = useCallback(
    async (code: string): Promise<string> => {
      try {
        console.log('Fetching access token for Auth0...')
        const api = `${process.env.REACT_APP_UNSW_AUTH_API_URL}/auth0/token`
        const response = await fetch(api, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
          }),
        })

        if (!response.ok) {
          throw new Error(
            `Failed to fetch access token for Auth0: ${response.statusText}`,
          )
        }

        console.log('Access token for Auth0 fetched successfully')
        const data = await response.json()
        localStorage.setItem(UNSW_AUTH_STORAGE_KEY, JSON.stringify(data))
        const token = data.access_token
        return token
      } catch (error) {
        console.error('Error fetching access token for Auth0:', error)
        throw new Error('Unable to fetch access token for Auth0')
      }
    },
    [],
  )

  const handleError = (
    error: string | null,
    errorDescription: string | null,
  ) => {
    console.error('Error during authentication:', error, errorDescription)
    throw new Error(`Authentication error: ${error} - ${errorDescription}`)
  }

  const handleMissingParams = useCallback(() => {
    console.warn(
      'Missing required parameters, restoring access token from storage...',
    )

    // redirect to login page
    loginWithRedirect()
  }, [])

  const exchangeCodeForToken = useCallback(
    async (code: string, type: string, codeVerifier: string | null) => {
      let accessToken: string | null = null

      if (type === 'auth0') {
        console.log('Type is auth0, fetching access token for Auth0...')
        accessToken = await getAccessTokenForAuth0(code)
      } else {
        if (!codeVerifier) {
          handleMissingParams()
          return
        }
        console.log(`Type is ${type}, fetching access token...`)
        accessToken = await getAccessToken(code, codeVerifier, type)
      }

      if (accessToken) {
        setAccessToken(accessToken)
        const targetUrlValue = getTargetUrl()
        console.log('Redirecting to target URL:', targetUrlValue || '/')
        navigate(targetUrlValue || '/', { replace: true })
      } else {
        console.warn('Failed to get access token')
      }
    },
    [
      getAccessToken,
      getAccessTokenForAuth0,
      handleMissingParams,
      navigate,
      setAccessToken,
    ],
  )

  const handleAuthRedirectCallback = useCallback(async (): Promise<void> => {
    const params = new URLSearchParams(window.location.search)

    try {
      const hasError = params.has('error')
      const hasCode = params.has('code')
      const hasType = params.has('type')

      if (hasError) {
        const error = params.get('error')
        const errorDescription = params.get('error_description')
        handleError(error, errorDescription)
      }

      if (!hasCode || !hasType) {
        handleMissingParams()
        return
      }

      const code = params.get('code') || ''
      const type = params.get('type') || ''
      const codeVerifier = params.get('code_verifier') || null

      await exchangeCodeForToken(code, type, codeVerifier)
    } catch (error) {
      console.error('Error in handleAuthRedirectCallback:', error)
      loginWithRedirect()
    }
  }, [exchangeCodeForToken, handleMissingParams])

  const isInIframe = (): boolean => {
    try {
      return window.self !== window.top
    } catch (e) {
      // If accessing window.top throws a SecurityError, assume we're in an iframe
      console.warn('Error checking if in iframe:', e)
      return true
    }
  }

  const initializeAuth = useCallback(async () => {
    console.log('Initializing UNSW Auth...')
    try {
      if (isInIframe()) {
        const iframeAuthSuccess = await handleIframeAuth(setAccessToken)
        if (iframeAuthSuccess) return
      }

      const token = restoreAccessTokenFromStorage()
      if (!token || !isValidToken(token)) {
        // remove the token from local storage if it is invalid
        localStorage.removeItem(UNSW_AUTH_STORAGE_KEY)
        console.log('No valid access token found, handleAuthRedirectCallback')
        await handleAuthRedirectCallback()
      }
    } catch (err) {
      console.error(err)
      setAuthState((prev) => ({
        ...prev,
        error:
          err instanceof Error ? err : new Error('An unknown error occurred'),
      }))
    } finally {
      setAuthState((prev) => ({ ...prev, isLoading: false }))
    }
  }, [
    handleAuthRedirectCallback,
    restoreAccessTokenFromStorage,
    setAccessToken,
  ])

  const loginWithRedirect = async (options?: {
    mode: string
  }): Promise<void> => {
    const mode = options?.mode || ''
    const params = mode ? `&mode=${encodeURIComponent(mode)}` : ''
    const state = generateState() // Generate a random state value
    const url = `${
      process.env.REACT_APP_UNSW_AUTH_URL
    }?redirect_uri=${encodeURIComponent(
      window.location.origin,
    )}${params}&state=${encodeURIComponent(state)}` // Append state to the URL
    console.log('Redirecting to UNSW Auth login page with state:', url)
    window.location.href = url
  }

  const logout = () => {
    console.log('Logging out from UNSW Auth...')
    localStorage.removeItem(UNSW_AUTH_STORAGE_KEY)
    setAccessToken(null)
    window.location.href = `${
      process.env.REACT_APP_UNSW_AUTH_URL
    }/auth/logout?redirect_uri=${encodeURIComponent(window.location.origin)}`
  }

  const getAccessTokenSilently = async (): Promise<string> => {
    // console.log('Getting access token silently...')
    if (accessTokenRef.current && isValidToken(accessTokenRef.current)) {
      return accessTokenRef.current
    }
    const token = restoreAccessTokenFromStorage()
    if (token) {
      if (isValidToken(token)) {
        accessTokenRef.current = token
        return token
      } else {
        console.warn('Access token is invalid, refreshing...')
        const refreshToken = getRefreshToken()
        if (refreshToken) {
          const newToken = await refreshAccessToken(refreshToken)
          accessTokenRef.current = newToken
          return newToken
        }
      }
    }
    throw new Error('No access token found')
  }

  const withAuthenticationRequired = <P extends object>(
    Component: ComponentType<P>,
    options?: {
      onRedirecting?: () => React.ReactNode
      returnTo?: string | (() => string)
    },
  ): FC<P> => {
    const WrappedComponent: FC<P> = (props) => {
      const defaultReturnTo = `${window.location.pathname}${window.location.search}`
      const returnTo = options?.returnTo || defaultReturnTo
      const onRedirecting =
        options?.onRedirecting ||
        (() => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <Spinner />
          </div>
        ))

      useEffect(() => {
        console.log(
          'withAuthenticationRequired::useEffect',
          authState.isLoading,
          authState.isAuthenticated,
        )
        if (authState.isLoading || authState.isAuthenticated) {
          console.log('No need to redirect, already authenticated or loading')
          return
        }
        const targetUrl = typeof returnTo === 'function' ? returnTo() : returnTo
        console.log('Redirecting to:', targetUrl)
        setTargetUrl(targetUrl)

        const mode = new URLSearchParams(window.location.search).get('mode')
        const options = {
          mode: mode || '',
        }

        loginWithRedirect(options)
      }, [returnTo])

      if (!authState.isAuthenticated) {
        return <>{onRedirecting()}</>
      }

      return <Component {...props} />
    }

    return WrappedComponent
  }

  useEffect(() => {
    console.log('UnswAuthProvider::useEffect')
    initializeAuth()
  }, [initializeAuth])

  return (
    <UnswAuthContext.Provider
      value={{
        ...authState,
        initializeAuth,
        loginWithRedirect,
        logout,
        getAccessTokenSilently,
        getRole,
        withAuthenticationRequired,
        hasPermission,
      }}
    >
      {children}
    </UnswAuthContext.Provider>
  )
}

export const useUnswAuth = () => {
  const context = useContext(UnswAuthContext)
  if (!context) {
    throw new Error('useUnswAuth must be used within an UnswAuthProvider')
  }
  return context
}
